import React from 'react';
import { Landmark, FileSpreadsheet, Scale, BookOpen, CheckCircle2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import ModernModuleDashboard from '../../shared/components/dashboard/ModernModuleDashboard';
import {
  buildDashboardCharts,
  pipelineFromKpis,
  withKpiVariants,
} from '../../shared/components/dashboard/moduleDashboardCharts';
import { formatBrandMoney } from '../../shared/utils/brandCurrency';

export default function AccountingDashboardPage() {
  const { t } = useTranslation();
  const p = (key, opts) => t(`modules.comptabilite.pages.dashboardPage.${key}`, opts);

  return (
    <ModernModuleDashboard
      apiEndpoint="/comptabilite/dashboard"
      title={p('title', { defaultValue: 'لوحة تحكم المحاسبة' })}
      subtitle={p('subtitle', { defaultValue: 'مؤشرات القيود والحسابات' })}
      icon={Landmark}
      accent="from-emerald-700 to-emerald-900"
      recentTitle={p('recent_entries', { defaultValue: 'آخر القيود' })}
      quickLinks={[
        { label: p('link_entries', { defaultValue: 'القيود' }), href: '/comptabilite/entries', icon: FileSpreadsheet },
        { label: p('link_chart', { defaultValue: 'دليل الحسابات' }), href: '/comptabilite/chart-accounts', icon: BookOpen },
        { label: p('link_balance', { defaultValue: 'ميزان المراجعة' }), href: '/comptabilite/balance-generale', icon: Scale },
        { label: p('link_analytics', { defaultValue: 'التحليلات' }), href: '/comptabilite/analyses-rapports', icon: Landmark },
      ]}
      mapData={(data) => {
        const kpis = data.kpis || {};
        const chartRows = data.chartRows || [];

        return {
          hero: {
            label: p('kpi_balance', { defaultValue: 'الرصيد الحالي' }),
            value: formatBrandMoney(kpis.currentBalance || 0),
            icon: Landmark,
          },
          kpis: withKpiVariants([
            { label: p('kpi_entries_month', { defaultValue: 'قيود الشهر' }), value: kpis.entriesMonth ?? 0, icon: FileSpreadsheet },
            { label: p('kpi_pending', { defaultValue: 'قيود معلقة' }), value: kpis.pendingApprovals ?? 0, icon: Scale },
            { label: p('kpi_accounts', { defaultValue: 'عدد الحسابات' }), value: kpis.totalAccounts ?? 0, icon: BookOpen },
            {
              label: p('kpi_posted_entries', { defaultValue: 'قيود معتمدة' }),
              value: kpis.validatedEntries ?? Math.max(0, (kpis.entriesMonth ?? 0) - (kpis.pendingApprovals ?? 0)),
              icon: CheckCircle2,
            },
          ]),
          charts: buildDashboardCharts({
            pipeline: {
              title: p('chart_pipeline_title', { defaultValue: 'ملخص المحاسبة' }),
              subtitle: p('chart_pipeline_subtitle', { defaultValue: 'قيود وحسابات معلقة' }),
              alwaysShow: true,
              rows: pipelineFromKpis([
                { label: p('kpi_entries_month', { defaultValue: 'قيود الشهر' }), value: kpis.entriesMonth ?? 0 },
                { label: p('kpi_pending', { defaultValue: 'قيود معلقة' }), value: kpis.pendingApprovals ?? 0 },
                { label: p('kpi_accounts', { defaultValue: 'عدد الحسابات' }), value: kpis.totalAccounts ?? 0 },
              ]),
            },
            status: {
              title: p('chart_status_title', { defaultValue: 'حالة القيود' }),
              subtitle: p('chart_status_subtitle', { defaultValue: 'معتمدة مقابل معلقة' }),
              active: kpis.validatedEntries ?? 0,
              inactive: kpis.pendingApprovals ?? 0,
              alwaysShow: true,
            },
            ranked: {
              title: p('chart_accounts', { defaultValue: 'توزيع الحسابات' }),
              subtitle: p('chart_accounts_subtitle', { defaultValue: 'حسب نوع الحساب' }),
              rows: chartRows,
            },
          }),
          recentItems: data.recentItems || [],
        };
      }}
    />
  );
}
